<template>
  <template v-if="$route.name === 'questions.index'">
   <div v-can="'questions.display'">
    <QuestionFilter
      :filters="filters"
      @update:filters="updateFilters"
      @reset-filters="resetFilters"
      @add="navigateToAdd"
    />

    <QuestionTable
      :questions="questions"
      :loading="isLoading"
      @delete-question="openDeleteDialog"
      
    />

    <PaginationControls
      v-if="pagination.totalItems > 0 && !isLoading"
      :pagination="pagination"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />

    <QuestionDeleteDialog ref="deleteDialog" @delete="deleteQuestion" />
    </div>
  </template>
  <router-view></router-view>
</template><script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useStore } from "vuex";
import QuestionTable from "@/components/features/questions/QuestionTable.vue";
import QuestionFilter from "@/components/features/questions/QuestionFilter.vue";
import QuestionDeleteDialog from "@/components/features/questions/dialogs/QuestionDeleteDialog.vue";
import PaginationControls from "@/components/common/PaginationControls.vue";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();
const router = useRouter();
const toast = useToast();
const store = useStore();
const deleteDialog = ref(null);
const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === 'teacher');

/* ---------- state ---------- */
const questions = ref([]);
const isLoading = ref(false);
const pagination = ref({
  currentPage: 1,
  perPage: 15,
  totalItems: 0,
  totalPages: 0,
  from: 0,
  to: 0
});
const filters = ref({
  search: "",
  subject_id: null,
  chapter_subject_id: null,
  question_type_id: null,
  question_level_id: null,
  class_id: null
});

/* ---------- helpers: consistent error toasts ---------- */
function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden')); // <- show Forbidden explicitly
  return toast.error(extractApiMessage(error, fallbackKey));
}

/* ---------- methods ---------- */
const updateFilters = (newFilters) => {
  filters.value = { ...filters.value, ...newFilters };
  pagination.value.currentPage = 1;
  fetchQuestions();
};

const resetFilters = () => {
  filters.value = {
    search: "",
    subject_id: null,
    chapter_subject_id: null,
    question_type_id: null,
    question_level_id: null,
    class_id: null
  };
  fetchQuestions();
};

const fetchQuestions = async () => {
  isLoading.value = true;
  try {
    const params = {
      page: pagination.value.currentPage,
      per_page: pagination.value.perPage,
      ...filters.value
    };

    if (isTeacher.value) {
      params.teacher_id = currentUser.value.id;
    }

    const response = await api.get("/questions", { params });
    questions.value = response.data?.data ?? [];

    if (response.data?.meta) {
      pagination.value = {
        currentPage: response.data.meta.current_page,
        perPage: response.data.meta.per_page,
        totalItems: response.data.meta.total,
        totalPages: response.data.meta.last_page,
        from: response.data.meta.from,
        to: response.data.meta.to
      };
    } else {
      // if no meta returned, at least reflect count
      pagination.value.totalItems = questions.value.length;
      pagination.value.totalPages = 1;
      pagination.value.from = questions.value.length ? 1 : 0;
      pagination.value.to = questions.value.length;
    }
  } catch (error) {
    notifyError(error, 'questions.errorFetchingQuestions'); // <- shows Forbidden on 403
    console.error('fetchQuestions error:', error);
  } finally {
    isLoading.value = false;
  }
};

const handlePageChange = (page) => {
  pagination.value.currentPage = page;
  fetchQuestions();
};

const handlePerPageChange = (perPage) => {
  pagination.value.perPage = perPage;
  pagination.value.currentPage = 1;
  fetchQuestions();
};

const openDeleteDialog = (question) => {
  deleteDialog.value?.open(question);
};

const deleteQuestion = async (id) => {
  try {
    await api.delete(`/questions/${id}`);
    toast.success(t("questions.deleteSuccess"));
    await fetchQuestions();
  } catch (error) {
    // if 403 -> Forbidden toast, else specific or fallback key
    if (error?.response?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden'));
    } else {
      notifyError(error, 'questions.deleteError');
    }
    console.error('deleteQuestion error:', error);
  }
};

const navigateToAdd = () => {
  router.push({ name: "questions.create" });
};

/* ---------- lifecycle ---------- */
onMounted(() => {
  fetchQuestions();
});

watch(filters, () => {
  fetchQuestions();
}, { deep: true });
</script>
